import React, { useLayoutEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faXmark } from "@fortawesome/pro-regular-svg-icons";
import { OverlayTrigger, Tooltip } from "react-bootstrap";

import { TCustomerType } from "redux/reducers/models/facility";

const MAX_TAG_LINES = 3;

type TBookingFeeCustomerTypeTagsProps = {
  types: TCustomerType[];
  onRemove?: (id: number) => void;
};

function getTypeLabel(type: TCustomerType): string {
  return type?.full_title || type?.title || "";
}

function measureVisibleCount(container: HTMLElement, total: number, maxLines: number): number {
  const tags = container.querySelectorAll<HTMLElement>("[data-booking-fee-tag]");

  if (tags.length === 0) {
    return total;
  }

  const lineAtIndex: number[] = [];
  let line = 0;
  let previousTop = -1;

  tags.forEach((tag, index) => {
    const top = tag.offsetTop;
    if (index === 0 || top !== previousTop) {
      line++;
      previousTop = top;
    }
    lineAtIndex[index] = line;
  });

  let overflowIndex = total;

  for (let index = 0; index < lineAtIndex.length; index++) {
    if (lineAtIndex[index] > maxLines) {
      overflowIndex = index;
      break;
    }
  }

  if (overflowIndex >= total) {
    return total;
  }

  return Math.max(1, overflowIndex - 1);
}

export default function BookingFeeCustomerTypeTags(props: TBookingFeeCustomerTypeTagsProps) {
  const { t } = useTranslation();
  const { types, onRemove } = props;

  const measureRef = useRef<HTMLDivElement>(null);
  const [visibleCount, setVisibleCount] = useState(types.length);

  useLayoutEffect(() => {
    const measureEl = measureRef.current;

    if (!measureEl || types.length === 0) {
      setVisibleCount(types.length);
      return;
    }

    const updateVisibleCount = () => {
      setVisibleCount(measureVisibleCount(measureEl, types.length, MAX_TAG_LINES));
    };

    updateVisibleCount();

    const observer = new ResizeObserver(() => {
      updateVisibleCount();
    });

    observer.observe(measureEl);

    return () => {
      observer.disconnect();
    };
  }, [types]);

  const hiddenTypes = types.slice(visibleCount);
  const hiddenCount = hiddenTypes.length;

  function renderTag(type: TCustomerType, options?: { forMeasure?: boolean }) {
    const label = getTypeLabel(type);

    return (
      <span
        key={type.id}
        className="booking-fees__customer-type-tag"
        data-booking-fee-tag
        data-for-measure={options?.forMeasure ? "true" : undefined}
      >
        <span className="booking-fees__customer-type-tag-text">{label}</span>
        {onRemove ? (
          <button
            type="button"
            className="booking-fees__customer-type-tag-remove"
            aria-label={t("Remove {{label}}", { label })}
            onClick={() => onRemove(type.id)}
          >
            <FontAwesomeIcon icon={faXmark} />
          </button>
        ) : null}
      </span>
    );
  }

  return (
    <div className="booking-fees__customer-type-tags">
      <div ref={measureRef} className="booking-fees__customer-type-tags-measure" aria-hidden="true">
        {types.map(type => renderTag(type, { forMeasure: true }))}
      </div>

      <div className="booking-fees__customer-type-tags-list">
        {types.slice(0, visibleCount).map(type => renderTag(type))}
        {hiddenCount > 0 ? (
          <OverlayTrigger
            placement="top"
            overlay={
              <Tooltip id="booking-fee-customer-types-overflow" className="booking-fees__tooltip">
                {hiddenTypes.map(type => getTypeLabel(type)).join(", ")}
              </Tooltip>
            }
          >
            <span className="booking-fees__customer-type-tags-more" tabIndex={0}>
              +{hiddenCount} {t("more")}
            </span>
          </OverlayTrigger>
        ) : null}
      </div>
    </div>
  );
}
